import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { motion } from 'framer-motion';
import { WalletIcon, CheckCircleIcon, XCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const HeaderWalletConnect = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletBalance, setWalletBalance] = useState('');
  const [networkName, setNetworkName] = useState('');
  const [error, setError] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [walletType, setWalletType] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  // Check if wallet is already connected on mount
  useEffect(() => {
    const checkExistingConnection = async () => {
      if (typeof window.ethereum !== 'undefined' && window.ethereum.selectedAddress) {
        try {
          const provider = new ethers.BrowserProvider(window.ethereum);
          const signer = await provider.getSigner();
          const address = await signer.getAddress();
          const balance = await provider.getBalance(address);
          const network = await provider.getNetwork();
          
          setWalletAddress(address);
          setWalletBalance(ethers.formatEther(balance));
          setNetworkName(network.name === 'unknown' ? `Chain ID: ${network.chainId}` : network.name);
          setIsConnected(true);
          setWalletType(detectWalletType());
        } catch (err) {
          console.error('Error checking existing connection:', err);
        }
      }
    };

    checkExistingConnection();
  }, []);

  // Detect wallet type
  const detectWalletType = () => {
    if (typeof window.ethereum === 'undefined') return 'none';
    
    if (window.ethereum.isMetaMask) return 'MetaMask';
    if (window.ethereum.isTrust) return 'Trust Wallet';
    if (window.ethereum.isCoinbaseWallet) return 'Coinbase Wallet';
    if (window.ethereum.isBraveWallet) return 'Brave Wallet';
    if (window.ethereum.isPhantom) return 'Phantom';
    
    return 'Unknown Wallet';
  };

  // Try different connection methods with timeout
  const tryConnectMethods = async () => {
    const walletType = detectWalletType();
    setWalletType(walletType);
    console.log('Detected wallet type:', walletType);

    // Helper function to add timeout to requests
    const withTimeout = (promise, ms = 10000) => {
      return Promise.race([
        promise,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), ms)
        )
      ]);
    };

    // Method 1: Try eth_requestAccounts first
    try {
      console.log('Trying eth_requestAccounts...');
      const accounts = await withTimeout(
        window.ethereum.request({ method: 'eth_requestAccounts' }),
        15000 // 15 second timeout for user interaction
      );
      if (accounts.length > 0) {
        console.log('eth_requestAccounts successful:', accounts);
        return accounts;
      }
    } catch (err) {
      console.log('eth_requestAccounts failed:', err.message);
      
      // If it's a MetaMask error, try to provide more specific guidance
      if (window.ethereum.isMetaMask && err.message.includes('Unexpected error')) {
        console.log('MetaMask unexpected error detected, this might be a browser/extension issue');
      }
    }

    // Method 2: Try eth_accounts (if already authorized)
    try {
      console.log('Trying eth_accounts...');
      const accounts = await withTimeout(
        window.ethereum.request({ method: 'eth_accounts' }),
        5000 // 5 second timeout for quick check
      );
      if (accounts.length > 0) {
        console.log('eth_accounts successful:', accounts);
        return accounts;
      }
    } catch (err) {
      console.log('eth_accounts failed:', err.message);
    }

    // Method 2.5: Check if user has already authorized this site
    if (window.ethereum.selectedAddress) {
      console.log('Found previously selected address:', window.ethereum.selectedAddress);
      return [window.ethereum.selectedAddress];
    }

    // Method 3: Try legacy enable method (for older wallets)
    if (window.ethereum.enable) {
      try {
        console.log('Trying legacy enable method...');
        const accounts = await withTimeout(
          window.ethereum.enable(),
          10000 // 10 second timeout
        );
        if (accounts.length > 0) {
          console.log('Legacy enable successful:', accounts);
          return accounts;
        }
      } catch (err) {
        console.log('Legacy enable failed:', err.message);
      }
    }

    throw new Error('No connection method worked');
  };

  // Connect wallet function with improved error handling
  const connectWallet = async () => {
    console.log('connectWallet called');
    
    if (typeof window.ethereum === 'undefined') {
      console.log('No window.ethereum found');
      setError('No wallet detected. Please install a Web3 wallet extension.');
      return;
    }

    // Check for MetaMask-specific issues
    if (window.ethereum.isMetaMask) {
      console.log('MetaMask detected, checking for common issues...');
      
      // Check if MetaMask is locked
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length === 0) {
          console.log('MetaMask appears to be locked or no accounts available');
        }
      } catch (err) {
        console.log('MetaMask check failed:', err.message);
      }
    }

    console.log('window.ethereum found:', window.ethereum);
    setIsConnecting(true);
    setError('');

    try {
      // Try multiple connection methods
      const accounts = await tryConnectMethods();
      
      if (accounts.length === 0) {
        throw new Error('No accounts found');
      }

      console.log('Creating provider...');
      const provider = new ethers.BrowserProvider(window.ethereum);
      console.log('Provider created:', provider);
      
      console.log('Getting signer...');
      const signer = await provider.getSigner();
      console.log('Signer received:', signer);
      
      console.log('Getting address...');
      const address = await signer.getAddress();
      console.log('Address received:', address);
      
      // Get balance and network info
      console.log('Getting balance...');
      const balance = await provider.getBalance(address);
      console.log('Balance received:', balance);
      
      console.log('Getting network...');
      const network = await provider.getNetwork();
      console.log('Network received:', network);
      
      setWalletAddress(address);
      setWalletBalance(ethers.formatEther(balance));
      setNetworkName(network.name === 'unknown' ? `Chain ID: ${network.chainId}` : network.name);
      setIsConnected(true);
      setShowDropdown(false);
      
      // Store connection info
      localStorage.setItem('walletConnected', 'true');
      localStorage.setItem('walletAddress', address);
      localStorage.setItem('walletType', walletType);
      
      console.log('Wallet connection successful!');
      
    } catch (err) {
      console.error('Wallet connection error:', err);
      
      // Provide specific error messages based on error type
      let errorMessage = 'Failed to connect wallet';
      
      if (err.code === 4001) {
        errorMessage = 'User rejected the connection request';
      } else if (err.code === -32002) {
        errorMessage = 'Connection request already pending. Please check your wallet.';
      } else if (err.message.includes('User rejected')) {
        errorMessage = 'Connection was rejected by the user';
      } else if (err.message.includes('No connection method worked')) {
        errorMessage = 'Unable to connect. Please try refreshing the page or reconnecting your wallet.';
      } else if (err.message.includes('Unexpected error')) {
        errorMessage = 'MetaMask encountered an unexpected error. Try refreshing the page or restarting MetaMask.';
      } else if (err.message.includes('Request timeout')) {
        errorMessage = 'Connection request timed out. MetaMask may be unresponsive. Try refreshing the page.';
      } else if (err.message.includes('evmAsk.js')) {
        errorMessage = 'MetaMask extension error. Try restarting MetaMask or refreshing the page.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    setWalletAddress('');
    setWalletBalance('');
    setNetworkName('');
    setIsConnected(false);
    setShowDropdown(false);
    setError('');
    
    localStorage.removeItem('walletConnected');
    localStorage.removeItem('walletAddress');
  };

  // Event listeners for wallet changes
  useEffect(() => {
    if (!isConnected || !walletAddress) return;

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        disconnectWallet();
      } else if (accounts[0] !== walletAddress) {
        setWalletAddress(accounts[0]);
        localStorage.setItem('walletAddress', accounts[0]);
      }
    };

    const handleChainChanged = () => {
      window.location.reload();
    };

    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, [isConnected, walletAddress]);

  // Format wallet address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // If connected, show wallet info dropdown
  if (isConnected) {
    return (
      <div className="relative">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowDropdown(!showDropdown)}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm inline-flex items-center gap-2"
        >
          <CheckCircleIcon className="w-4 h-4" />
          {formatAddress(walletAddress)}
          <ChevronDownIcon className="w-4 h-4" />
        </motion.button>

        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 dark:text-white">Wallet Connected</h3>
                <button
                  onClick={disconnectWallet}
                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                >
                  Disconnect
                </button>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Wallet:</span>
                  <span className="text-gray-900 dark:text-white font-medium">
                    {walletType}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Address:</span>
                  <span className="font-mono text-sm text-gray-900 dark:text-white">
                    {formatAddress(walletAddress)}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Balance:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {parseFloat(walletBalance).toFixed(4)} ETH
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Network:</span>
                  <span className="text-gray-900 dark:text-white">{networkName}</span>
                </div>
              </div>
              
              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => window.location.href = '/web3-connect'}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors font-medium text-sm"
                >
                  Complete Application
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    );
  }

  // If not connected, show connect button
  return (
    <div className="relative">
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={connectWallet}
        disabled={isConnecting}
        className="bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <WalletIcon className="w-4 h-4" />
        {isConnecting ? 'Connecting...' : 'Connect Wallet'}
      </motion.button>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute right-0 mt-2 w-80 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 z-50"
        >
          <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-2">{error}</p>
          
          {error.includes('MetaMask') && (
            <div className="mt-2 space-y-2">
              <p className="text-xs text-red-500">Try these steps:</p>
              <ol className="text-xs text-red-500 list-decimal list-inside space-y-1">
                <li>Refresh the page</li>
                <li>Restart MetaMask extension</li>
                <li>Check if MetaMask is unlocked</li>
                <li>Try switching networks in MetaMask</li>
              </ol>
            </div>
          )}
          
          <div className="mt-3 pt-2 border-t border-red-200 dark:border-red-700 space-y-2">
            <button
              onClick={() => {
                setError('');
                setRetryCount(prev => prev + 1);
                connectWallet();
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors font-medium text-sm"
            >
              Retry Connection ({retryCount + 1})
            </button>
            
            <a
              href="https://metamask.io/download/"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Install MetaMask →
            </a>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default HeaderWalletConnect;
